import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { getScoringConfig } from '../api/client';
import { FALLBACK_SCORING_CONFIG, FALLBACK_SLIDER_RANGES } from './scoringConfig';

const STORAGE_KEY = 'menagtm.scoringConfig.v1';
const ScoringConfigContext = createContext(null);

function loadStoredOverride() {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

/** Backend uses snake_case; the frontend config/equations use camelCase. */
function toFrontendConfig(serverConfig) {
  return {
    weights: { ...serverConfig.weights },
    halfLifeDays: serverConfig.half_life_days,
    corroborationMinFamilies: serverConfig.corroboration_min_families,
    corroborationBonus: serverConfig.corroboration_bonus,
    corroborationWindowDays: serverConfig.corroboration_window_days,
    tierAMin: serverConfig.tier_a_min,
    tierBMin: serverConfig.tier_b_min,
    saturationK: serverConfig.saturation_k,
  };
}

export function ScoringConfigProvider({ children }) {
  // Read once at mount — this is the user's own prior tuning, if any.
  const storedOverride = useRef(loadStoredOverride()).current;

  const [serverDefaults, setServerDefaults] = useState(FALLBACK_SCORING_CONFIG);
  const [sliderRanges, setSliderRanges] = useState(FALLBACK_SLIDER_RANGES);
  const [config, setConfig] = useState(() =>
    storedOverride
      ? {
          ...FALLBACK_SCORING_CONFIG,
          ...storedOverride,
          weights: { ...FALLBACK_SCORING_CONFIG.weights, ...(storedOverride.weights || {}) },
        }
      : FALLBACK_SCORING_CONFIG
  );
  const [justSaved, setJustSaved] = useState(false);

  // Sync defaults from the backend — the real single source of truth for
  // default values (app/config/scoring_config.py). If the user already has
  // a locally-tuned config, leave their live values alone; only their
  // "Reset to defaults" target changes.
  useEffect(() => {
    getScoringConfig()
      .then((serverConfig) => {
        const defaults = toFrontendConfig(serverConfig);
        setServerDefaults(defaults);
        if (serverConfig.slider_ranges) setSliderRanges(serverConfig.slider_ranges);
        if (!storedOverride) setConfig(defaults);
      })
      .catch(() => {
        // Backend unreachable — keep using the local fallback constants.
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handle = setTimeout(() => {
      try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(config));
        setJustSaved(true);
        const clear = setTimeout(() => setJustSaved(false), 1400);
        return () => clearTimeout(clear);
      } catch {
        // localStorage unavailable (e.g. private browsing quota) — config
        // still works for this session, it just won't persist.
      }
    }, 400);
    return () => clearTimeout(handle);
  }, [config]);

  function updateWeight(family, value) {
    setConfig((c) => ({ ...c, weights: { ...c.weights, [family]: value } }));
  }

  function updateField(field, value) {
    setConfig((c) => ({ ...c, [field]: value }));
  }

  function resetToDefaults() {
    setConfig(serverDefaults);
  }

  return (
    <ScoringConfigContext.Provider
      value={{ config, sliderRanges, updateWeight, updateField, resetToDefaults, justSaved }}
    >
      {children}
    </ScoringConfigContext.Provider>
  );
}

export function useScoringConfig() {
  const ctx = useContext(ScoringConfigContext);
  if (!ctx) throw new Error('useScoringConfig must be used within ScoringConfigProvider');
  return ctx;
}
